'use client'

import axios from 'axios'

import {
  CustomAuthenticationResult,
  ResolveMentionSuggestionsArgs,
  ResolveUsersArgs,
} from '@liveblocks/core'
import { LiveblocksProvider } from '@liveblocks/react/suspense'

import { useSvcLiveblocksContext } from '@dc-shared-web/providers/SvcLiveblocksProvider'

import { ResolveRoomsInfoArgs } from '@liveblocks/client'
import { UserNotificationProvider } from '@/app/core/providers/UserNotificationProvider'

type SvcLiveblocksGetUserResponse = {
  users: {
    uuid: string
    email: string
    username: string
    firstName: string | null
    lastName: string | null
  }[]
  total: number
}

type SvcLiveblocksGetSearchMentionsResponse = {
  id: string
}[]

type SvcLiveblocksPostRoomsInfoResponse = {
  id: string
  crunchName: string
  checklistName: string
}[]

export default function CommentingRootProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const svcLiveblocksClient = useSvcLiveblocksContext()
  const authEndpointHandler = async (
    roomId?: string
  ): Promise<CustomAuthenticationResult> => {
    try {
      const { data } =
        await svcLiveblocksClient.post<CustomAuthenticationResult>(
          '/api/auth',
          {
            roomId,
          }
        )

      return data
    } catch (error) {
      // TODO: Rollbar logging

      return axios.isAxiosError(error)
        ? {
            error: error.code || 'unknown',
            reason: error.message,
          }
        : {
            error: 'unknown',
            reason: 'An unknown error occurred',
          }
    }
  }

  const resolveUsersHandler = async ({ userIds }: ResolveUsersArgs) => {
    try {
      const { data } =
        await svcLiveblocksClient.get<SvcLiveblocksGetUserResponse>(
          '/api/users',
          {
            params: { uuids: userIds.join(',') },
          }
        )

      return data.users.map((user) => ({
        id: user.uuid,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
      }))
    } catch (error) {
      // TODO: Rollbar logging

      return []
    }
  }

  const resolveMentionSuggestionsHandler = async ({
    text,
    roomId,
  }: ResolveMentionSuggestionsArgs) => {
    try {
      const { data } =
        await svcLiveblocksClient.get<SvcLiveblocksGetSearchMentionsResponse>(
          '/api/mentions/search',
          {
            params: { searchText: text.trim(), roomId },
          }
        )

      return data.map((user) => user.id)
    } catch (error) {
      return []
    }
  }

  const resolveRoomsInfoHandler = async ({ roomIds }: ResolveRoomsInfoArgs) => {
    const roomsInfo =
      await svcLiveblocksClient.post<SvcLiveblocksPostRoomsInfoResponse>(
        '/api/rooms/resolve-info',
        { roomIds }
      )
    return roomsInfo.data.map((room) => ({
      id: room.id,
      name: `${room.checklistName} in ${room.crunchName}`,
      url: '',
      slug: '',
    }))
  }

  return (
    <LiveblocksProvider
      authEndpoint={authEndpointHandler}
      resolveUsers={resolveUsersHandler}
      resolveRoomsInfo={resolveRoomsInfoHandler}
      resolveMentionSuggestions={resolveMentionSuggestionsHandler}
    >
      <UserNotificationProvider>{children}</UserNotificationProvider>
    </LiveblocksProvider>
  )
}
